import {TagIcon} from '@primer/octicons-react'
import type React from 'react'
import {Fragment} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueEventRationales$data} from './__generated__/IssueEventRationales.graphql'
import type {LabeledEvent$key} from './__generated__/LabeledEvent.graphql'
import type {UnlabeledEvent$key} from './__generated__/UnlabeledEvent.graphql'
import {IssueEventRationale} from './IssueEventRationale'
import {Label} from './Label'
import styles from './labels.module.css'
import {RolledupLabeledEvent} from './RolledupLabeledEvent'
import {TimelineRow} from './row/TimelineRow'

type LabeledEventProps = {
  queryRef: LabeledEvent$key
  rollupGroup?: Record<string, Array<LabeledEvent$key | UnlabeledEvent$key>>
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLDivElement | null>
  rationales?: Record<string, IssueEventRationales$data['eventRationales'][number]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LabeledEventFragment = graphql`
  fragment LabeledEvent on LabeledEvent {
    id
    databaseId
    createdAt
    actor {
      ...TimelineRowEventActor
    }
    label {
      ...LabelData
    }
    rationale {
      rationale
    }
  }
`

export function LabeledEvent({
  queryRef,
  rollupGroup,
  issueUrl,
  onLinkClick,
  timelineEventBaseUrl,
  highlightedEventId,
  refAttribute,
  rationales,
}: LabeledEventProps): React.ReactElement {
  const {actor, createdAt, databaseId, id, rationale: fragmentRationale} = useFragment(LabeledEventFragment, queryRef)
  const highlighted = String(databaseId) === highlightedEventId

  // Prefer rationale from the Issue.eventRationales lookup (initial load), but
  // fall back to the inlined rationale so live subscription updates also carry it.
  const rationale =
    rationales?.[id] ?? (fragmentRationale ? {rationale: fragmentRationale.rationale, issueEvent: null} : undefined)
  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={TagIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupLabeledEvent
            rollupGroup={rollupGroup}
            timelineEventBaseUrl={timelineEventBaseUrl}
            rationales={rationales}
          />
        ) : (
          <>
            <AddedLabelsRendering queryRefs={[queryRef]} timelineEventBaseUrl={timelineEventBaseUrl} />
            {rationale?.rationale && (
              <IssueEventRationale
                rationale={rationale.rationale}
                issueEventDatabaseId={String(databaseId)}
                analyticsAction={'ISSUE_LABEL_ADDED'}
              />
            )}
          </>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedLabelsRendering = ({
  queryRefs,
  timelineEventBaseUrl,
  rationales,
}: Pick<LabeledEventProps, 'timelineEventBaseUrl' | 'rationales'> & {queryRefs: LabeledEvent$key[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.added} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalAddedLabelRendering
            queryRef={queryRef}
            timelineEventBaseUrl={timelineEventBaseUrl}
            rationales={rationales}
          />{' '}
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedLabelRendering = ({
  queryRef,
  timelineEventBaseUrl,
  rationales,
}: Pick<LabeledEventProps, 'queryRef' | 'timelineEventBaseUrl' | 'rationales'>) => {
  const {label, databaseId, id, rationale: fragmentRationale} = useFragment(LabeledEventFragment, queryRef)
  const rationale =
    rationales?.[id] ?? (fragmentRationale ? {rationale: fragmentRationale.rationale, issueEvent: null} : undefined)

  return (
    <div className={styles.labelContainer}>
      <Label queryRef={label} timelineEventBaseUrl={timelineEventBaseUrl} />
      {rationale?.rationale && (
        <IssueEventRationale
          rationale={rationale.rationale}
          issueEventDatabaseId={String(databaseId)}
          analyticsAction={'ISSUE_LABEL_ADDED'}
        />
      )}
    </div>
  )
}
